import { useEffect, useState } from "react";
import { Bot, FileText, LoaderCircle, MessageSquare, Mic } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { api } from "@/lib/api";

const AGENT_LABELS = { coach: "Coach civique", demarches: "Assistant démarches", interprete: "Interprète" };

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="card-integra p-5" data-testid={testid}>
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-bleu-light text-bleu"><Icon className="h-4 w-4" /></span>
      <p className="mt-3 font-display text-2xl font-extrabold text-slate-900">{value}</p>
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
    </div>
  );
}

export default function AdminAI() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/ai").then((r) => setData(r.data))
      .catch(() => toast.error("Impossible de charger les statistiques IA."));
  }, []);

  if (!data) return <div className="flex justify-center py-16"><LoaderCircle className="h-6 w-6 animate-spin text-slate-300" /></div>;

  return (
    <div className="mx-auto max-w-4xl space-y-5" data-testid="page-admin-ai">
      <div>
        <h1 className="flex items-center gap-2.5 font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">
          <Bot className="h-7 w-7 text-bleu" /> Intelligence artificielle
        </h1>
        <p className="mt-1 text-sm text-slate-500">Utilisation des agents IA (Coach civique, Assistant démarches, Interprète). Les clés IA ne sont jamais exposées.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat icon={MessageSquare} label="Messages au total" value={data.messages_total} testid="admin-ai-total" />
        <Stat icon={MessageSquare} label="Messages (30 j)" value={data.messages_30d} testid="admin-ai-30d" />
        <Stat icon={FileText} label="Documents traduits" value={data.interpreter_docs} testid="admin-ai-docs" />
        <Stat icon={Mic} label="Courriers générés" value={data.courriers} testid="admin-ai-courriers" />
      </div>

      <div className="card-integra p-5" data-testid="admin-ai-by-agent">
        <h2 className="text-sm font-bold text-slate-900">Par agent</h2>
        <div className="mt-3 space-y-2">
          {data.by_agent.map((a) => (
            <div key={a.agent} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-2.5">
              <span className="text-xs font-bold text-slate-700">{AGENT_LABELS[a.agent] || a.agent}</span>
              <span className="text-xs text-slate-500">
                {a.count} message{a.count > 1 ? "s" : ""}
                {a.last && ` · dernier le ${new Date(a.last).toLocaleDateString("fr-FR")}`}
              </span>
            </div>
          ))}
          {data.by_agent.length === 0 && <p className="text-xs text-slate-400">Aucun message IA pour le moment.</p>}
        </div>
      </div>

      {data.daily_30d.length > 0 && (
        <div className="card-integra p-5" data-testid="admin-ai-chart">
          <h2 className="text-sm font-bold text-slate-900">Messages par jour (30 derniers jours)</h2>
          <div className="mt-3 h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.daily_30d}>
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} width={28} />
                <Tooltip />
                <Bar dataKey="count" fill="#1E3FAE" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="card-integra p-5" data-testid="admin-ai-top-users">
        <h2 className="text-sm font-bold text-slate-900">Utilisateurs les plus actifs</h2>
        <div className="mt-3 space-y-1.5">
          {data.top_users.map((u, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg px-3 py-2 odd:bg-slate-50">
              <span className="truncate text-xs font-semibold text-slate-700">{u.name || u.email || "—"}</span>
              <span className="shrink-0 text-xs text-slate-500">{u.count} messages</span>
            </div>
          ))}
          {data.top_users.length === 0 && <p className="text-xs text-slate-400">Aucune activité IA.</p>}
        </div>
      </div>
    </div>
  );
}
